/* eslint-disable max-len */
import React from 'react';

import { DAC_DOCS_BASE_URL, DAC_FAQS_DOC_LINK } from '@/constants';
import { CRUDableType } from '@/meta';
import { localNumberFormat } from '@/utils';

import { LOW_TRAFFIC_THRESHOLD_RANGE } from '../new-slo-views/expanded/low-traffic-helper/constants';

import DiscoveryButton from './discovery-button';

/* eslint-disable react/no-unescaped-entities */
// prettier-ignore
export const helperCopy = {
  team: {
    name: (
      <>
        The Team is responsible for ensuring that the Capability's SLOs are met, and will be
        paged if the Capability incurs an SLO breach.
      </>
    ),
    opsgenieTeam: (
      <>
        The name of the Opsgenie team that will receive alerts for Capability SLO breaches
        and burns.
      </>
    ),
    atlassianOrgTeam: (
      <>
        Associate and verify the link between your TOME Team and the Atlassian
        Organisation Team.
        <br />
        Still not sure? Check out our{' '}
        <a
          href={`${DAC_DOCS_BASE_URL}teams/#connecting-an-atlassian-org-team-to-a-tome-team`}
          target="_blank"
          rel="noopener noreferrer"
        >
          docs
        </a>
        .
      </>
    ),
    discovery: <DiscoveryButton url={`${DAC_DOCS_BASE_URL}teams/`} />
  },
  product: {
    name: (
      <>
        A Product is a grouping of Capabilities representing functionality that could
        theoretically be productionised as a vendor offering to Atlassian. The name of the
        Product should succinctly describe it.
      </>
    ),
    discovery: <DiscoveryButton url={`${DAC_DOCS_BASE_URL}products/`} />
  },
  capability: {
    name: (
      <>
        A Capability is an end-to-end user experience or a set of related functionality.
        The name of the Capability should succinctly describe it.
      </>
    ),
    upstreamDependencies: (
      <>
        Existing Tome Capabilities that this Capability depends on. When listing another Capability
        as <em>upstream</em>, this Capability will be listed as a <em>downstream</em> dependency for
        that Capability.
      </>
    ),
    owner: (
      <>
        The person who bears ultimate responsibility for SLO breaches for this Capability.
        They are also the first point of contact for this Capability.
        Typically a team lead or product manager.
      </>
    ),
    discovery: <DiscoveryButton url={`${DAC_DOCS_BASE_URL}capabilities/`} />
  },
  pollinator: {},
  pollinatorArchetype: {},
  slogroup:{},
  slo: {
    name: (
      <>
        An SLO represents a promise to your users that your Capability will work a certain way a
        certain percentage of the time. The name of the SLO should succinctly describe what is being
        measured.
      </>
    ),
    sloType: (
      <>
        <em>Reliability</em> focuses on how often something works correctly.
        <br />
        <em>Performance</em> focuses on how quickly something is done.
      </>
    ),
    runbookUrl: (
      <>
        The Runbook of an SLO is referenced when
        responding to alerts generated by an SLO.
        When an alert is triggered by this SLO, a link to this Runbook will appear in the description.
      </>
    ),
    serviceId: (
      <>
        Enter a valid Compass Service ID
      </>
    ),
    signalFlowGoodQueries: (
      <>
        This is a SignalFlow program that returns a <em>count</em> of <em>good</em> events.
        Not sure where to start? Check out our <a href={`${DAC_DOCS_BASE_URL}tutorials/sli-signalflow/`}>tutorial</a>.
      </>
    ),
    signalFlowBadQueries: (
      <>
        This is a SignalFlow program that returns a <em>count</em> of <em>bad</em> events.
        Not sure where to start? Check out our <a href={`${DAC_DOCS_BASE_URL}tutorials/sli-signalflow/`}>tutorial</a>.
      </>
    ),
    mimirGoodQueries: (
      <>
        This is a Mimir program that returns a <em>count</em> of <em>good</em> events.
      </>
    ),
    mimirBadQueries: (
      <>
        This is a Mimir program that returns a <em>count</em> of <em>bad</em> events.
      </>
    ),
    sloTarget: (
      <>
        The percentage of time your users can expect what's measured to be working at an acceptable level.
      </>
    ),
    lowTrafficThreshold: (
      <>
        The threshold we use to designate an SLO as low traffic. You can set a value from <b>0</b> to our largest
        supported value of <b>{localNumberFormat(LOW_TRAFFIC_THRESHOLD_RANGE[1])}</b>.
        <br/><br/>If left blank or unset, the
        the <a target="_blank" rel="noopener noreferrer" href={`${DAC_FAQS_DOC_LINK}low-traffic-slos/#what-is-the-default-low-traffic-threshold-`}>default</a> threshold value will be applied.
      </>
    ),
    suppressLowTrafficAlerts: (
      <>
        If checked, no alerts will be triggered when the traffic is below the low traffic threshold.
        <br/><br/>
        <a target="_blank" rel="noopener noreferrer" href={`${DAC_FAQS_DOC_LINK}low-traffic-slos/#what-happens-when-an-slo-is-in-low-traffic-state-`}>Read more</a> in our docs.
      </>
    ),
    isUltraSteepBurnEnabled: (
      <>
        If checked, an ultra steep burn alert (5-minute) will be triggered with an automatically determined burn rate threshold.
        <br/><br/>
        <a target="_blank" rel="noopener noreferrer" href={`${DAC_DOCS_BASE_URL}alerts/#ultra-steep`}>Read more</a> in our docs.
      </>
    ),
    tags: (
      <>
        If you need to split your SLOs, you can use these fields to keep structured information.
      </>
    ),
    tagsCriticalPath: (
      <>
        Mark Yes if this SLO is a Critical Path SLO. Definition of Critical Path SLO is provided{' '}
        <a target="_blank" rel="noopener noreferrer" href="https://hello.atlassian.net/wiki/spaces/MSR/pages/3222999128/Product+Critical+Path+SLO+List">
          here
        </a>.
      </>
    ),
    tagsCoveredExperiences: (
      <>
        Select one or more covered experiences that can be impacted by this SLO. <br/><br/>Visit{' '}
        <a target="_blank" rel="noopener noreferrer" href="https://go/coveredexperiences">
          go/coveredexperiences
        </a>{' '}to learn more about covered experiences.
      </>
    ),
    discovery: <DiscoveryButton url={`${DAC_DOCS_BASE_URL}slos/`} />
  }
} as Record<CRUDableType, Record<string, React.AllHTMLAttributes<HTMLElement>>>;
/* eslint-enable react/no-unescaped-entities */
